import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { CheckIcon, InstagramIcon, YoutubeIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { SectionLabel } from './SectionLabel';
const COMMUNITY_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/c390a23f-b648-4b77-a0a6-aa5b56dc2e54.jpg';

/** Formspree form — signups are delivered to jennawing@parnasociety.com */
const FORM_ID = 'xzepnjag';
const SIGNUP_EMAIL = 'mailto:jennawing@parnasociety.com?subject=Join%20the%20PAR%E1%B9%86A%20newsletter';
const SOCIALS = [{
  label: 'Instagram',
  href: 'https://www.instagram.com/parnasociety?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==',
  Icon: InstagramIcon
}, {
  label: 'YouTube',
  href: 'https://www.youtube.com/@PAR%E1%B9%86ASOCIETY',
  Icon: YoutubeIcon
}];
export function Connect() {
  const [email, setEmail] = useState('');
  const [state, handleSubmit] = useForm(FORM_ID);
  const formErrors = state.errors?.getFormErrors?.() ?? [];
  const hasFieldError = (state.errors?.getFieldErrors?.('email') ?? []).length > 0;
  return <section id="connect" className="grain relative w-full overflow-hidden" data-id="element-12" data-mp="1q4c09gcaj2wg">
      <img src={COMMUNITY_IMAGE} alt="The afterglow of a sunset over still water, blush and gold fading into lavender" className="absolute inset-0 h-full w-full object-cover" data-id="element-13" data-mp="1uvkagelg2sao" />
      <div className="absolute inset-0 bg-cream/75" aria-hidden="true" data-id="element-14" data-mp="24thz3377ur3t" />

      <div className="relative mx-auto max-w-2xl px-6 py-28 text-center md:px-10 md:py-36" data-id="element-15" data-mp="24vupm4r1i9sh">
        <Reveal data-id="element-16" data-mp="29qqit880j376">
          <SectionLabel data-id="element-17" data-mp="29r06uq9hz0a8">Stay Connected</SectionLabel>
        </Reveal>
        <Reveal delay={0.08} data-id="element-18" data-mp="2dtwooer7qsd4">
          <h2 className="mt-7 font-display text-4xl font-light leading-[1.15] text-ink sm:text-5xl" data-id="element-19" data-mp="fetn332jfb6b">
            Join the <span className="italic text-clay" data-id="element-20" data-mp="jg5ffgcsqai7">PARṆA</span> community
          </h2>
        </Reveal>
        <Reveal delay={0.16} data-id="element-21" data-mp="1mr2rviuciifj">
          <p className="mx-auto mt-6 max-w-md text-[15px] font-light leading-[1.9] text-muted" data-id="element-22" data-mp="1msdtpd48dg40">
            Sign up for our newsletter to receive class updates, retreat announcements, invitations
            to community gatherings, and gentle reminders to breathe.
          </p>
        </Reveal>

        <Reveal delay={0.24} data-id="element-23" data-mp="1rpcntvnw9n27">
          <div className="mx-auto mt-12 min-h-[120px] max-w-md" data-id="element-24" data-mp="1rp4bq5zlils2">
            <AnimatePresence mode="wait" data-id="element-25" data-mp="25p5btj81dp6h">
              {state.succeeded ? <motion.div key="success" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex flex-col items-center gap-4 py-4" data-id="element-26" data-mp="20rlxkjbc9cej">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-clay text-cream" data-id="element-27" data-mp="2ahp0pcc6c6ql">
                    <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-28" data-mp="2oelqrbkgxk6f" />
                  </span>
                  <p role="status" className="font-display text-xl font-light italic text-ink" data-id="element-29" data-mp="333dmwgadooqi">
                    You&rsquo;re subscribed. Welcome to the community.
                  </p>
                </motion.div> : <motion.form key="form" onSubmit={handleSubmit} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="text-left" data-id="element-30" data-mp="2u3gvi6wltbrr">
                  <label htmlFor="email" className="sr-only" data-id="element-31" data-mp="2p4489l1tm2on">
                    Email address
                  </label>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-0 sm:border-b sm:border-clay/30" data-id="element-32" data-mp="2afr7marsfvnt">
                    <input id="email" name="email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="your@email.com" aria-invalid={hasFieldError} className="w-full border-b border-clay/30 bg-transparent py-3 text-[15px] font-light text-ink placeholder:text-muted/60 focus:border-clay focus:outline-none sm:border-none sm:py-4" data-id="element-33" data-mp="2fe2imottkc7m" />
                    <button type="submit" disabled={state.submitting} className="shrink-0 whitespace-nowrap py-3 text-[10px] font-light uppercase tracking-ultra text-clay transition-opacity duration-300 hover:opacity-70 disabled:opacity-50 sm:pl-6" data-id="element-34" data-mp="1kkrvtqhnds77">
                      {state.submitting ? 'Sending…' : 'Subscribe'}
                    </button>
                  </div>

                  <ValidationError prefix="Email" field="email" errors={state.errors} className="mt-3 text-[12px] font-light text-clay" data-id="element-35" data-mp="302ae0ambv30s" />

                  {formErrors.length > 0 && <p role="status" className="mt-3 text-[12px] font-light leading-relaxed text-clay" data-id="element-36" data-mp="mr9465w7tcgt">
                      Something went wrong on our end. Please{' '}
                      <a href={SIGNUP_EMAIL} className="underline underline-offset-4" data-id="element-37" data-mp="rkbve7dis9tu">
                        email us
                      </a>{' '}
                      and we&rsquo;ll add you ourselves.
                    </p>}
                </motion.form>}
            </AnimatePresence>
          </div>
        </Reveal>

        <Reveal delay={0.32} data-id="element-38" data-mp="7t6v5g6ezys6">
          <ul className="mt-8 flex justify-center gap-8" data-id="element-39" data-mp="7sx5xut71vnj">
            {SOCIALS.map(social => <li key={social.label} data-id="element-40" data-mp="cumjpi7swnfb">
                <a href={social.href} target="_blank" rel="noopener noreferrer" aria-label={social.label} className="flex items-center gap-2 text-[10px] uppercase tracking-wider2 text-muted transition-colors duration-300 hover:text-clay" data-id="element-41" data-mp="1j9ne3jh6gxjs">
                  <social.Icon className="h-4 w-4" strokeWidth={1.25} aria-hidden="true" data-id="element-42" data-mp="3g9hv33fpy7rt" />
                  <span className="hidden sm:inline" data-id="element-43" data-mp="3u8hnmm6cr6t9">{social.label}</span>
                </a>
              </li>)}
          </ul>
        </Reveal>
      </div>
    </section>;
}